/**
 * useTempProfile hook - Read OAuth profile from temp_profile cookie
 * @implements TS-016 - Product Owner Registration UI
 * @implements FS-001.1.1 - Product Owner Registration
 *
 * This hook reads and decodes the temp_profile JWT cookie set by the
 * backend OAuth callback. The cookie contains profile information from
 * the OAuth provider (GitHub/Google) that is used to pre-fill the
 * registration form.
 *
 * The temp_profile cookie is NOT httpOnly, allowing JavaScript to read it.
 * It is a short-lived JWT used only during the registration flow.
 */

import { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

/**
 * Temporary OAuth profile from the temp_profile cookie
 * @implements FS-001.2.1 - OAuth Authentication (Profile Data)
 */
export interface TempProfile {
  /** OAuth provider used for authentication */
  provider: 'github' | 'google';
  /** Provider-specific user ID */
  providerId: string;
  /** User email from OAuth provider */
  email: string;
  /** Display name from OAuth provider */
  name: string;
  /** Avatar URL from OAuth provider (optional) */
  avatarUrl?: string;
}

/**
 * Result type for the useTempProfile hook
 */
export interface UseTempProfileResult {
  /** Decoded profile data or null if not available */
  profile: TempProfile | null;
  /** Whether the hook is still loading/parsing the cookie */
  isLoading: boolean;
  /** Error message if profile could not be read */
  error: string | null;
  /** Function to clear the temp_profile cookie */
  clearProfile: () => void;
}

/**
 * Cookie name used by the backend OAuth callback
 */
const TEMP_PROFILE_COOKIE = 'temp_profile';

/**
 * Parse a cookie value from document.cookie
 * @param name The cookie name to find
 * @returns The cookie value or null if not found
 */
function getCookieValue(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...valueParts] = cookie.trim().split('=');
    if (cookieName === name) {
      // Rejoin in case value contains '='
      return valueParts.join('=');
    }
  }
  return null;
}

/**
 * Delete a cookie by setting its expiration to the past
 * @param name The cookie name to delete
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Hook to read and decode the temp_profile JWT cookie
 * @implements TS-016 - Product Owner Registration UI
 * @implements FS-001.1.1 - Product Owner Registration
 *
 * This hook is used on the registration page to read OAuth profile data
 * that was set by the backend after a successful OAuth callback.
 *
 * @example
 * const { profile, isLoading, error, clearProfile } = useTempProfile();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <SessionExpired onRetry={() => window.location.href = '/'} />;
 * if (!profile) return <GitHubOAuthButton flow="register_po" />;
 *
 * // Use profile data to pre-fill form
 * return <RegistrationForm email={profile.email} name={profile.name} />;
 */
export function useTempProfile(): UseTempProfileResult {
  const [profile, setProfile] = useState<TempProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Read temp_profile cookie (httpOnly: false, so JS can read it)
      const jwt = getCookieValue(TEMP_PROFILE_COOKIE);

      if (!jwt) {
        setError(
          'No OAuth session found. Please start the registration flow again.',
        );
        setIsLoading(false);
        return;
      }

      // Decode the JWT to get profile data
      // Note: We don't verify the signature here - that's the backend's job
      // We're just reading the payload for display purposes
      const decoded = jwtDecode<TempProfile>(jwt);

      // Validate required fields
      if (!decoded.email || !decoded.provider || !decoded.providerId) {
        setError('Invalid OAuth session data. Please try again.');
        setIsLoading(false);
        return;
      }

      setProfile(decoded);
    } catch (err) {
      // JWT decode errors (malformed token, expired, etc.)
      console.error(
        '[useTempProfile] Failed to decode temp_profile cookie:',
        err,
      );
      setError('Invalid or expired OAuth session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear the temp_profile cookie
   * Called after successful registration or when user wants to restart the flow
   */
  const clearProfile = useCallback(() => {
    deleteCookie(TEMP_PROFILE_COOKIE);
    setProfile(null);
  }, []);

  return { profile, isLoading, error, clearProfile };
}
